import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmtIDR, fmtDate } from "../lib/format";
import { PageHeader, StatCard, Card, Badge, Btn, statusTone } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Users, Map, Wallet, Stamp, Boxes, Camera, TrendingUp, TrendingDown, GripVertical, Eye, EyeOff, Save, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

const PIE_COLORS = { tersedia: "#10b981", booking: "#f59e0b", terjual: "#f43f5e", hold: "#6366f1" };

const WIDGET_DEFS = [
  { key: "tasks", label: "Tugas Saya" },
  { key: "kpi", label: "KPI Utama" },
  { key: "unit_pie", label: "Status Unit Siteplan" },
  { key: "pipeline_bar", label: "Pipeline CRM" },
  { key: "cashflow", label: "Arus Kas Bulan Ini" },
  { key: "projects", label: "Progres Proyek" },
  { key: "task_monitor", label: "Monitoring To-Do User" },
  { key: "agenda", label: "Agenda Terdekat" },
];

const WIDGET_LINKS = { unit_pie: "/penjualan", pipeline_bar: "/crm", cashflow: "/akuntansi", projects: "/proyek", tasks: "/todo", task_monitor: "/todo", agenda: "/kalender" };

const DEFAULT_WIDGETS = WIDGET_DEFS.map((w) => w.key);

export default function Dashboard() {
  const { projectId, projects, user } = useAuth();
  const navigate = useNavigate();
  const canMonitor = ["Super Admin", "Direktur", "Global Admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [monFilter, setMonFilter] = useState("");
  const [agenda, setAgenda] = useState([]);
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [editMode, setEditMode] = useState(false);
  const [dragKey, setDragKey] = useState(null);

  const load = useCallback(() => {
    const q = projectId ? `?project_id=${projectId}` : "";
    api.get(`/dashboard/summary${q}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/tasks${q}`).then((r) => setTasks(r.data.filter((t) => t.status === "todo").slice(0, 12))).catch(() => {});
    if (canMonitor) api.get("/tasks/monitor").then((r) => setAllTasks(r.data)).catch(() => {});
    const month = new Date().toISOString().slice(0, 7);
    api.get(`/calendar/events?month=${month}${projectId ? `&project_id=${projectId}` : ""}`).then((r) => {
      const todayStr = new Date().toISOString().slice(0, 10);
      setAgenda(r.data.filter((e) => e.date >= todayStr).slice(0, 6));
    }).catch(() => {});
    api.get("/dashboard/layout").then((r) => { const saved = r.data.widgets?.length ? r.data.widgets : DEFAULT_WIDGETS; const merged = [...saved, ...DEFAULT_WIDGETS.filter((w) => !saved.includes(w))]; setWidgets(["tasks", ...merged.filter((w) => w !== "tasks")]); }).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const saveLayout = async () => {
    await api.put("/dashboard/layout", { widgets });
    setEditMode(false);
    toast.success("Layout dashboard disimpan");
  };

  const removeWidget = (key) => setWidgets(widgets.filter((w) => w !== key));
  const addWidget = (key) => setWidgets([...widgets, key]);
  const onDropWidget = (target) => {
    if (!dragKey || dragKey === target) return;
    const arr = widgets.filter((w) => w !== dragKey);
    arr.splice(arr.indexOf(target), 0, dragKey);
    setWidgets(arr);
    setDragKey(null);
  };

  if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">Memuat dashboard...</div>;

  const WTitle = ({ k, className, children }) => (
    <button onClick={() => navigate(WIDGET_LINKS[k])} className={`${className} hover:text-emerald-300 transition-colors inline-flex items-center gap-1 text-left`} data-testid={`widget-link-${k}`}>
      {children}<ChevronRight className="w-3.5 h-3.5 text-slate-500" />
    </button>
  );

  const pieData = Object.entries(data.unit_summary).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));
  const stageData = Object.entries(data.lead_by_stage).map(([k, v]) => ({ name: k, leads: v }));
  const activeProject = projects.find((p) => p.id === projectId);
  const hiddenWidgets = DEFAULT_WIDGETS.filter((w) => !widgets.includes(w));

  const renderWidget = (key) => {
    switch (key) {
      case "kpi": {
        const kpis = [
          { t: "stat-total-leads", l: "Total Leads CRM", v: data.leads_total, icon: Users, tone: "cyan", to: "/crm" },
          { t: "stat-unit-terjual", l: "Unit Terjual", v: `${data.unit_summary.terjual || 0}/${data.units_total}`, icon: Map, tone: "emerald", sub: fmtIDR(data.sold_value), to: "/penjualan" },
          { t: "stat-kas-masuk", l: "Kas Masuk (Bulan Ini)", v: fmtIDR(data.cash_in_month), icon: TrendingUp, tone: "emerald", to: "/akuntansi" },
          { t: "stat-kas-keluar", l: "Kas Keluar (Bulan Ini)", v: fmtIDR(data.cash_out_month), icon: TrendingDown, tone: "rose", to: "/akuntansi" },
          { t: "stat-pending-approval", l: "Approval Pending", v: data.pending_approvals, icon: Stamp, tone: "amber", to: "/approvals" },
          { t: "stat-low-stock", l: "Item Stok Minimum", v: data.low_stock_items, icon: Boxes, tone: "rose", to: "/inventory" },
          { t: "stat-absensi-hari-ini", l: "Absensi Hari Ini", v: data.attendance_today, icon: Camera, tone: "indigo", to: "/absensi" },
          { t: "stat-proyek-aktif", l: "Proyek Aktif", v: data.projects.filter((p) => p.status === "berjalan").length, icon: Wallet, tone: "cyan", to: "/proyek" },
        ];
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((k) => (
              <div key={k.t} onClick={() => navigate(k.to)} className="cursor-pointer hover:opacity-80 transition-opacity" data-testid={`kpi-link-${k.t}`}>
                <StatCard testid={k.t} label={k.l} value={k.v} icon={k.icon} tone={k.tone} sub={k.sub} />
              </div>
            ))}
          </div>
        );
      }
      case "unit_pie":
        return (
          <Card testid="chart-unit-status">
            <WTitle k="unit_pie" className="text-sm font-medium text-slate-300 mb-4">Status Unit Siteplan</WTitle>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {pieData.map((d) => <Cell key={d.name} fill={PIE_COLORS[d.name] || "#64748b"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-3 flex-wrap mt-2">
              {pieData.map((d) => <Badge key={d.name} tone={statusTone(d.name)}>{d.name}: {d.value}</Badge>)}
            </div>
          </Card>
        );
      case "pipeline_bar":
        return (
          <Card testid="chart-lead-pipeline">
            <WTitle k="pipeline_bar" className="text-sm font-medium text-slate-300 mb-4">Pipeline CRM per Tahap</WTitle>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stageData} layout="vertical">
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={95} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="leads" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        );
      case "cashflow":
        return (
          <Card testid="chart-cashflow">
            <WTitle k="cashflow" className="text-sm font-medium text-slate-300 mb-4">Arus Kas Bulan Ini</WTitle>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={[{ name: "Masuk", value: data.cash_in_month }, { name: "Keluar", value: data.cash_out_month }]}>
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                <Tooltip formatter={(v) => fmtIDR(v)} contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#0891b2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        );
      case "projects":
        return (
          <Card testid="project-progress-list">
            <WTitle k="projects" className="text-sm font-medium text-slate-300 mb-4">Progres Proyek</WTitle>
            {data.projects.length === 0 && <p className="text-slate-500 text-sm">Belum ada proyek.</p>}
            <div className="space-y-4">
              {data.projects.map((p) => (
                <div key={p.id} data-testid={`dashboard-project-${p.id}`}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-300">{p.name}</span>
                    <span className="font-mono text-emerald-400">{p.progress}%</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${p.progress}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      case "tasks":
        return (
          <Card testid="dashboard-tasks" className="border-emerald-500/30 !p-5">
            <WTitle k="tasks" className="text-base font-medium text-slate-200 mb-3">Tugas Aktif Saya</WTitle>
            {tasks.length === 0 && <p className="text-slate-500 text-sm">Tidak ada tugas aktif.</p>}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {tasks.map((t) => {
                const overdue = t.due_date && t.due_date < new Date().toISOString().slice(0, 10);
                return (
                <div key={t.id} onClick={() => navigate("/todo")} className={`flex items-center justify-between bg-slate-950 border rounded-lg px-3 py-2.5 cursor-pointer hover:border-emerald-500/40 transition-colors ${overdue ? "border-rose-500/60" : "border-slate-800"}`} data-testid={`dashboard-task-${t.id}`}>
                  <span className="text-sm text-slate-200 truncate">{t.title}</span>
                  {overdue && <span className="text-[10px] font-mono text-rose-400 font-bold shrink-0 ml-2">TERLAMBAT</span>}
                  {t.due_date && <span className={`text-[10px] font-mono shrink-0 ml-2 ${overdue ? "text-rose-400" : "text-amber-400"}`}>{fmtDate(t.due_date)}</span>}
                </div>
                );
              })}
            </div>
          </Card>
        );
      case "task_monitor": {
        const assignees = [...new Set(allTasks.map((t) => t.assignee_name))].sort();
        const filtered = monFilter ? allTasks.filter((t) => t.assignee_name === monFilter) : allTasks;
        const open = filtered.filter((t) => t.status !== "done");
        const done = filtered.filter((t) => t.status === "done");
        return (
          <Card testid="dashboard-task-monitor">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <WTitle k="task_monitor" className="text-sm font-medium text-slate-300">Monitoring To-Do List Semua User</WTitle>
              <select className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-200" value={monFilter} onChange={(e) => setMonFilter(e.target.value)} data-testid="task-monitor-filter">
                <option value="">Semua user ({filtered.length})</option>
                {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3 text-center">
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-2"><div className="text-[10px] font-mono uppercase text-slate-500">Belum Selesai</div><div className="font-mono text-lg text-amber-400" data-testid="monitor-open-count">{open.length}</div></div>
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-2"><div className="text-[10px] font-mono uppercase text-slate-500">Selesai</div><div className="font-mono text-lg text-emerald-400" data-testid="monitor-done-count">{done.length}</div></div>
            </div>
            {filtered.length === 0 && <p className="text-slate-500 text-sm">Belum ada tugas.</p>}
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {filtered.slice(0, 50).map((t) => (
                <div key={t.id} className="flex flex-wrap items-center gap-2 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`monitor-task-${t.id}`}>
                  <Badge tone={t.status === "done" ? "emerald" : t.priority === "tinggi" ? "rose" : "amber"}>{t.status === "done" ? "SELESAI" : "TODO"}</Badge>
                  <span className="text-xs text-slate-200 flex-1 min-w-[140px] truncate">{t.title}</span>
                  <span className="text-[10px] font-mono text-cyan-300">{t.assignee_name}</span>
                  {t.due_date && <span className="text-[10px] font-mono text-slate-500">{fmtDate(t.due_date)}</span>}
                </div>
              ))}
            </div>
          </Card>
        );
      }
      case "agenda":
        return (
          <Card testid="dashboard-agenda">
            <WTitle k="agenda" className="text-sm font-medium text-slate-300 mb-3">Agenda Terdekat</WTitle>
            {agenda.length === 0 && <p className="text-slate-500 text-sm">Tidak ada agenda bulan ini.</p>}
            <div className="space-y-2">
              {agenda.map((e) => (
                <div key={e.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`dashboard-agenda-${e.id}`}>
                  <span className="text-xs text-slate-300 truncate">{e.title}</span>
                  <span className="text-[10px] font-mono text-slate-500 shrink-0 ml-2">{fmtDate(e.date)}</span>
                </div>
              ))}
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard Eksekutif"
        subtitle={activeProject ? `Tampilan per proyek: ${activeProject.name}` : "Konsolidasi semua proyek — pilih proyek di kanan atas untuk segmentasi"}
        testid="dashboard-header"
      >
        <Btn tone={editMode ? "primary" : "ghost"} onClick={() => editMode ? saveLayout() : setEditMode(true)} data-testid="customize-dashboard-button">
          {editMode ? <><Save className="w-4 h-4" /> Simpan Layout</> : "Kustomisasi Widget"}
        </Btn>
      </PageHeader>

      {editMode && hiddenWidgets.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="widget-gallery">
          {hiddenWidgets.map((k) => (
            <Btn key={k} tone="ghost" className="!text-xs" onClick={() => addWidget(k)} data-testid={`widget-add-${k}`}>
              <Eye className="w-3.5 h-3.5" /> {WIDGET_DEFS.find((w) => w.key === k)?.label}
            </Btn>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {widgets.filter((key) => key !== "task_monitor" || canMonitor).map((key) => (
          <div
            key={key}
            draggable={editMode}
            onDragStart={() => setDragKey(key)}
            onDragOver={(e) => editMode && e.preventDefault()}
            onDrop={() => onDropWidget(key)}
            className={editMode ? "border border-dashed border-emerald-500/40 rounded-xl p-1.5" : ""}
            data-testid={`widget-container-${key}`}
          >
            {editMode && (
              <div className="flex items-center justify-between px-2 py-1 mb-1">
                <span className="flex items-center gap-2 text-xs text-slate-400"><GripVertical className="w-3.5 h-3.5 cursor-grab" /> {WIDGET_DEFS.find((w) => w.key === key)?.label} — seret untuk mengurutkan</span>
                <button onClick={() => removeWidget(key)} className="text-slate-500 hover:text-rose-400" data-testid={`widget-remove-${key}`}><EyeOff className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {renderWidget(key)}
          </div>
        ))}
      </div>
    </div>
  );
}
